import React, { useEffect, useMemo } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TreeSelect } from 'antd';
import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { useAppDispatch } from '@store/hooks';
import { fetchUnpaidInvoices } from '@store/Finance/AP/ApInvoices/UnpaidInvoicesSlice';
import { Card, CardBody, CardFooter, CardHeader } from '@components/card/card';

import { NumericFormat } from 'react-number-format';

export const ApplyDebitMemoComponent: React.FC<{
  formik: any;
  onClose: () => void;
  OperatingUnitOptions: any[];
  treeSuppliersData: any[];
}> = ({ formik, onClose, OperatingUnitOptions, treeSuppliersData }) => {
  const { values, setFieldValue } = formik;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // State خاص بالمكون
  const [standardInvoices, setStandardInvoices] = useState<any[]>([]);
  const [refundInvoices, setRefundInvoices] = useState<any[]>([]);
  const [selectedRefundInvoice, setSelectedRefundInvoice] = useState<any>(null);
  const [appliedStandardInvoices, setAppliedStandardInvoices] = useState<Map<string, number>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const resetState = () => {
    setStandardInvoices([]);
    setRefundInvoices([]);
    setSelectedRefundInvoice(null);
    setAppliedStandardInvoices(new Map());
    setFetchError(null);
    setIsLoading(false);
  };

  const handleGetUnpaidInvoices = async () => {
    if (!values.organizationId || !values.peopleId) return;

    setIsLoading(true);
    setFetchError(null);

    try {
      const [standardResult, refundResult] = await Promise.all([
        dispatch(
          fetchUnpaidInvoices({
            peopleId: values.peopleId,
            organizationId: values.organizationId,
            invoiceType: 'Standard'
          })
        ).unwrap(),
        dispatch(
          fetchUnpaidInvoices({
            peopleId: values.peopleId,
            organizationId: values.organizationId,
            invoiceType: 'CreditMemo'
          })
        ).unwrap()
      ]);

      let standardData: any[] = [];
      let refundData: any[] = [];

      if (standardResult.succeeded) {
        standardData = standardResult.data.map((invoice: any) => ({
          ...invoice,
          invoiceType: 'Standard',
          id: invoice.apPaymentScheduleId
        }));
        setStandardInvoices(standardData);
      }

      if (refundResult.succeeded) {
        refundData = refundResult.data.map((invoice: any) => ({
          ...invoice,
          invoiceType: 'Refund'
        }));
        setRefundInvoices(refundData);
      }

      if (!refundResult.succeeded || refundData.length === 0) {
        setFetchError('لا توجد فواتير مرتجعات للمقاصة');
      } else if (!standardResult.succeeded || standardData.length === 0) {
        setFetchError('لا توجد فواتير عادية للمقاصة');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setFetchError('فشل في جلب البيانات');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefundSelection = (invoice: any) => {
    setSelectedRefundInvoice(invoice);
  };

  const handleAppliedAmountChange = (invoiceId: string, amount: number) => {
    const invoice = standardInvoices.find(inv => inv.apPaymentScheduleId === invoiceId);
    if (!invoice) return;

    const maxAmount = invoice.remainingAmount;
    const finalAmount = Math.max(0, Math.min(amount, maxAmount));

    const updatedApplied = new Map(appliedStandardInvoices);

    if (finalAmount > 0) {
      updatedApplied.set(invoiceId, finalAmount);
    } else {
      updatedApplied.delete(invoiceId);
    }

    setAppliedStandardInvoices(updatedApplied);
  };

  const handleSubmit = () => {
    if (!selectedRefundInvoice) {
      alert('يجب اختيار فاتورة مرتجع واحدة');
      return;
    }

    if (appliedStandardInvoices.size === 0) {
      alert('يجب إدخال مبالغ للفواتير العادية');
      return;
    }

    const totalAppliedAmount = Array.from(appliedStandardInvoices.values()).reduce((sum, amount) => sum + amount, 0);
    const refundAmount = selectedRefundInvoice.remainingAmount;

    // السماح بتطبيق جزء من اشعار المدين (تم إزالة شرط المساواة)
    if (totalAppliedAmount > refundAmount) {
      alert(
        `المبلغ الإجمالي المطبق (${totalAppliedAmount.toLocaleString()}) لا يمكن أن يتجاوز مبلغ المردود (${refundAmount.toLocaleString()})`
      );
      return;
    }

    const appliedInvoicesData = Array.from(appliedStandardInvoices.entries()).map(([scheduleId, appliedAmount]) => {
      const invoice = standardInvoices.find(inv => inv.apPaymentScheduleId === scheduleId);
      return {
        id: null,
        apPaymentScheduleId: invoice.apPaymentScheduleId,
        invoiceNum: invoice.invoiceNum,
        invoiceDate: invoice.invoiceDate,
        dueDate: invoice.dueDate,
        invoiceAmount: invoice.invoiceAmount,
        schadualAmount: invoice.schadualAmount || 0,
        amount: appliedAmount,
        actualRemainingAmount: invoice.remainingAmount
      };
    });

    formik?.setFieldValue('scheduleList', appliedInvoicesData);
    formik?.setFieldValue('creditMemoInvoiceId', selectedRefundInvoice.invoiceId);
    formik?.setFieldValue('appliedCreditMemoAmount', totalAppliedAmount); // حفظ المبلغ المطبق

    onClose();
  };

  const columns = [
    { title: 'رقم الفاتورة', key: 'invoiceNum' },
    { title: 'التاريخ', key: 'invoiceDate' },
    { title: 'تاريخ الاستحقاق', key: 'dueDate' },
    { title: 'المبلغ الأصلي', key: 'originalAmount' },
    { title: 'الرصيد المتبقي', key: 'remainingAmount' },
    { title: 'المبلغ المطبق', key: 'appliedAmount' },
    { title: 'الرصيد بعد التطبيق', key: 'balanceAfter' }
  ];

  const getTableData = () => {
    return standardInvoices.map(invoice => {
      const appliedAmount = appliedStandardInvoices.get(invoice.apPaymentScheduleId) || 0;
      const remainingBalance = invoice.remainingAmount - appliedAmount;
      const balanceAfter = Math.max(0, remainingBalance);

      return {
        invoiceNum: (
          <div>
            <div className='fw-bold'>{invoice.invoiceNum}</div>
            <small className='text-muted'>دفع: {invoice.paymentNum}</small>
          </div>
        ),
        invoiceDate: <div className='text-center'>{invoice.invoiceDate?.split('T')[0]}</div>,
        dueDate: (
          <div
            className={`text-center ${new Date(invoice.dueDate) < new Date() ? 'text-danger fw-bold' : 'text-dark'}`}
          >
            {invoice.dueDate?.split('T')[0]}
            {new Date(invoice.dueDate) < new Date() && (
              <div>
                <small className='text-danger'>متأخرة</small>
              </div>
            )}
          </div>
        ),
        originalAmount: <div className='text-center text-muted'>{Number(invoice.invoiceAmount).toLocaleString()}</div>,
        remainingAmount: (
          <div className={`text-center fw-bold ${invoice.remainingAmount > 0 ? 'text-primary' : 'text-success'}`}>
            {Number(invoice.remainingAmount).toLocaleString()}
          </div>
        ),
        appliedAmount: (
          <div className='text-center'>
            {/* <Form.Control
              type='number'
              value={appliedAmount || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = parseFloat(e.target.value) || 0;
                handleAppliedAmountChange(invoice.apPaymentScheduleId, value);
              }}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                const value = parseFloat(e.target.value) || 0;
                handleAppliedAmountChange(invoice.apPaymentScheduleId, value);
              }}
              step='0.01'
              min='0'
              max={invoice.remainingAmount}
              className='text-center fw-bold border-primary'
              style={{ width: '130px' }}
              placeholder='0'
            /> */}
            <NumericFormat
              className='form-control text-center fw-bold border-primary'
              style={{ width: '130px' }}
              value={appliedAmount || ''}
              placeholder='0'
              thousandSeparator=',' // فواصل آلاف
              decimalSeparator='.' // عرض افتراضي
              allowedDecimalSeparators={[',', '.']} // ✅ يقبل (،) أو (.)
              decimalScale={2}
              fixedDecimalScale={false}
              allowNegative={false}
              // يمنع إدخال أكبر من المتبقي
              isAllowed={({ floatValue }) => {
                const v = floatValue ?? 0;
                return v >= 0 && v <= Number(invoice.remainingAmount || 0);
              }}
              onValueChange={({ floatValue }) => {
                handleAppliedAmountChange(invoice.apPaymentScheduleId, floatValue ?? 0);
              }}
            />
          </div>
        ),
        balanceAfter: (
          <div className={`text-center fw-bold ${balanceAfter > 0 ? 'text-warning' : 'text-success'}`}>
            {balanceAfter.toLocaleString()}
            {balanceAfter === 0 && (
              <div>
                <small className='text-success'>مستنفذ</small>
              </div>
            )}
          </div>
        )
      };
    });
  };

  const totalAppliedAmount = Array.from(appliedStandardInvoices.values()).reduce((sum, amount) => sum + amount, 0);
  const totalRefundAmount = selectedRefundInvoice ? selectedRefundInvoice.remainingAmount : 0;
  const differenceAmount = totalRefundAmount - totalAppliedAmount;

  // السماح بتطبيق جزء من اشعار المدين
  const isAmountValid = totalAppliedAmount <= totalRefundAmount;
  const isFormValid = selectedRefundInvoice && appliedStandardInvoices.size > 0 && isAmountValid;

  // تحديد لون التنبيه بناءً على الحالة
  const getAlertType = () => {
    if (totalAppliedAmount === 0) return 'alert-secondary';
    if (totalAppliedAmount === totalRefundAmount) return 'alert-success';
    if (totalAppliedAmount < totalRefundAmount) return 'alert-info';
    return 'alert-danger';
  };

  const getStatusText = () => {
    if (totalAppliedAmount === 0) return 'لم يتم تطبيق أي مبلغ';
    if (totalAppliedAmount === totalRefundAmount) return 'تم تطبيق اشعار المدين بالكامل';
    if (totalAppliedAmount < totalRefundAmount) return 'تم تطبيق جزء من اشعار المدين';
    return 'المبلغ المطبق يتجاوز اشعار المدين';
  };

  return (
    <>
      <div className='row g-3 mb-4'>
        {/* المورد والوحدة التشغيلية */}
        <div className='col-lg-5'>
          <div className='form-group'>
            <label className='form-label fw-bold'>المورد</label>
            <TreeSelect
              showSearch
              className='w-100'
              treeLine
              placeholder='اختر المورد'
              allowClear
              value={values.peopleId}
              onChange={option => {
                resetState();
                formik.setFieldValue('peopleId', option === null ? null : option);
              }}
              treeData={treeSuppliersData}
              treeNodeFilterProp='title'
              getPopupContainer={triggerNode => triggerNode.parentNode}
            />
          </div>
        </div>

        <div className='col-lg-5'>
          <div className='form-group'>
            <label className='form-label fw-bold'>الوحدة التشغيلية</label>
            <Select
              isSearchable={true}
              isClearable
              options={OperatingUnitOptions}
              value={OperatingUnitOptions?.find((f: any) => f.value === formik.values.organizationId)}
              onChange={(option: any) => {
                resetState();
                setFieldValue('organizationId', option === null ? null : option.value);
              }}
              placeholder='اختر الوحدة التشغيلية'
              className='react-select-container'
              classNamePrefix='react-select'
            />
          </div>
        </div>

        <div className='col-lg-2'>
          <div className='form-group h-100 d-flex flex-column justify-content-end'>
            <button
              disabled={isLoading || !values.organizationId || !values.peopleId}
              type='button'
              className='btn btn-sm btn-outline-theme w-100 py-2'
              onClick={handleGetUnpaidInvoices}
            >
              {isLoading ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2'></span>
                  جاري التحميل...
                </>
              ) : (
                <>
                  <i className='fas fa-download me-2'></i>
                  جلب الفواتير
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {fetchError && (
        <div className='alert alert-warning d-flex align-items-center mb-4' role='alert'>
          <i className='fas fa-exclamation-triangle me-2'></i>
          <div>{fetchError}</div>
        </div>
      )}

      {/* فواتير المرتجعات */}
      {refundInvoices.length > 0 && (
        <div className='row mb-4'>
          <div className='col-12'>
            <Card>
              <CardHeader className='bg-warning bg-opacity-25 text-white d-flex justify-content-between align-items-center'>
                <h6 className='mb-0'>
                  <i className='fas fa-rotate-left me-2'></i>
                  اختر فاتورة المردود (إشعار مدين)
                </h6>
                <span className='badge bg-light text-dark'>{refundInvoices.length} فاتورة</span>
              </CardHeader>
              <CardBody className='p-0'>
                <div className='list-group list-group-flush'>
                  {refundInvoices.map((invoice, index) => (
                    <div
                      key={invoice.apPaymentScheduleId}
                      className={`list-group-item list-group-item-action ${
                        selectedRefundInvoice?.apPaymentScheduleId === invoice.apPaymentScheduleId
                          ? 'active bg-warning bg-opacity-15 border-warning'
                          : ''
                      }`}
                      onClick={() => handleRefundSelection(invoice)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className='d-flex justify-content-between align-items-center'>
                        <div className='d-flex align-items-center'>
                          <div className='form-check me-3'>
                            <input
                              type='radio'
                              className='form-check-input'
                              name='refundInvoice'
                              checked={selectedRefundInvoice?.apPaymentScheduleId === invoice.apPaymentScheduleId}
                              onChange={() => handleRefundSelection(invoice)}
                            />
                          </div>
                          <div>
                            <h6 className='mb-1'>{invoice.invoiceNum}</h6>
                            <p className='mb-0 text-muted small'>
                              رقم الدفع: {invoice.paymentNum} | التاريخ: {invoice.invoiceDate}
                            </p>
                          </div>
                        </div>
                        <div
                          className={`fw-bold ${
                            selectedRefundInvoice?.apPaymentScheduleId === invoice.apPaymentScheduleId
                              ? 'text-dark'
                              : 'text-success'
                          }`}
                        >
                          {Number(invoice.remainingAmount).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* ملخص المقاصة */}
      {(selectedRefundInvoice || appliedStandardInvoices.size > 0) && (
        <div className={`alert ${getAlertType()} mb-4`}>
          <div className='row text-center'>
            <div className='col-md-3 border-end'>
              <div className='small text-muted'>مبلغ المردود</div>
              <div className='h5 fw-bold text-warning'>{totalRefundAmount.toLocaleString()}</div>
              {selectedRefundInvoice && (
                <small className='text-muted'>فاتورة: {selectedRefundInvoice.invoiceNum}</small>
              )}
            </div>
            <div className='col-md-3 border-end'>
              <div className='small text-muted'>المبلغ المطبق</div>
              <div className='h5 fw-bold text-primary'>{totalAppliedAmount.toLocaleString()}</div>
              <small className='text-muted'>على {appliedStandardInvoices.size} فاتورة</small>
            </div>
            <div className='col-md-3 border-end'>
              <div className='small text-muted'>المبلغ المتبقي</div>
              <div className={`h5 fw-bold ${differenceAmount > 0 ? 'text-info' : 'text-success'}`}>
                {differenceAmount.toLocaleString()}
              </div>
            </div>
            <div className='col-md-3'>
              <div className='small text-muted'>الحالة</div>
              <div className={`h6 fw-bold ${isAmountValid ? 'text-success' : 'text-danger'}`}>{getStatusText()}</div>
            </div>
          </div>
          {differenceAmount > 0 && (
            <div className='mt-2 text-center text-info'>
              <i className='fas fa-info-circle me-2'></i>
              يمكنك تطبيق الجزء المتبقي لاحقاً ({differenceAmount.toLocaleString()})
            </div>
          )}
          {!isAmountValid && (
            <div className='mt-2 text-center text-danger'>
              <i className='fas fa-exclamation-triangle me-2'></i>
              المبلغ المطبق لا يمكن أن يتجاوز مبلغ اشعار المدين
            </div>
          )}
        </div>
      )}

      {/* جدول الفواتير العادية */}
      {standardInvoices.length > 0 && (
        <div className='row'>
          <div className='col-12'>
            <Card className='border-primary'>
              <CardHeader className='bg-primary bg-opacity-35 text-white d-flex justify-content-between align-items-center'>
                <h6 className='mb-0'>
                  <i className='fas fa-file-invoice me-2'></i>
                  الفواتير العادية - أدخل المبالغ المطبقة
                </h6>
              </CardHeader>
              <CardBody className='p-0'>
                <div className='table-responsive'>
                  <table className='table table-hover mb-0'>
                    <thead className='table-primary'>
                      <tr>
                        {columns.map((col, index) => (
                          <th key={index} className='text-center small'>
                            {col.title}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {getTableData().map((row, rowIndex) => {
                        const invoiceId = standardInvoices[rowIndex]?.apPaymentScheduleId;
                        const appliedAmount = appliedStandardInvoices.get(invoiceId) || 0;

                        return (
                          <tr
                            key={rowIndex}
                            className={appliedAmount > 0 ? 'table-active bg-primary bg-opacity-10' : ''}
                          >
                            {columns.map((col, colIndex) => (
                              <td key={colIndex} className='text-center align-middle'>
                                {row[col.key as keyof typeof row]}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardBody>
              <CardFooter className='d-flex justify-content-between align-items-center'>
                <div className='text-muted small'>
                  {appliedStandardInvoices.size} / {standardInvoices.length} فاتورة مختارة
                </div>
                <div className='fw-bold text-primary'>الإجمالي المطبق: {totalAppliedAmount.toLocaleString()}</div>
              </CardFooter>
            </Card>
          </div>
        </div>
      )}

      <div className='border-top mt-4 pt-4'>
        <div className='d-flex justify-content-between align-items-center'>
          <div></div>
          <div className='d-flex'>
            <button disabled={!isFormValid} className='btn btn-theme me-2 px-4' onClick={handleSubmit}>
              <i className='fas fa-balance-scale me-2'></i>
              استمرار
            </button>
            <button
              type='button'
              className='btn btn-outline-secondary'
              onClick={() => navigate('/AP/ApPayments', { replace: true })}
            >
              <i className='fas fa-times me-2'></i>
              إلغاء
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
